import { ref as sRef, uploadBytesResumable, getDownloadURL, deleteObject, getBytes } from 'firebase/storage'
import { db, storage, functions } from '../firebase/firebase'
import { httpsCallable } from 'firebase/functions'
import {
  collection, addDoc, serverTimestamp, onSnapshot, query,
  orderBy, where, doc, setDoc, getDoc, updateDoc, getDocs,
  runTransaction, writeBatch, deleteDoc, limit, startAfter, Timestamp,
  getCountFromServer, collectionGroup, increment
} from 'firebase/firestore'
const APP_SETTINGS_KEY = "app_settings";
const APP_SETTINGS_CACHE_KEY = "app_settings_cache";
const MODELES_CACHE_KEY = "modeles_cache";
const DOS_REC_CACHE_KEY = "dossier_records_cache";
const DASHBOARD_SETTINGS_KEY = "dashboard_settings";
const DASHBOARD_SETTINGS_CACHE_KEY = "dashboard_settings_cache";
const dashboardSettingsCacheKey = (uid) => `${DASHBOARD_SETTINGS_CACHE_KEY}:${uid}`;
const REL_ROWS_CACHE_KEY = "relation_rows_cache";
const relationRowsCacheKey = (uid, typeKey) =>
  `${REL_ROWS_CACHE_KEY}:${uid}:${typeKey || "__all"}`;
// Clé de cache par utilisateur (évite d'afficher les réglages d'un autre compte sur le même navigateur)
const appSettingsCacheKey = (uid) => `${APP_SETTINGS_CACHE_KEY}:${uid}`;
const modelesCacheKey = (uid) => `${MODELES_CACHE_KEY}:${uid}`;
const dossierRecordsCacheKey = (uid, { dossierKey = "", statut = "" } = {}) =>
  `${DOS_REC_CACHE_KEY}:${uid}:${dossierKey || "__all"}:${statut || "__any"}`;

// -------- Paging par défaut (peut être surchargé au call) ----------
const PAGE_SIZES = {
  modeles: 20,           // derniers modèles
  dossierRecords: 30,    // derniers enregistrements
  relationRows: 50,      // dernières lignes relation
};

const DELETE_BATCH_SIZE = 250;

const collectStoragePaths = (value, acc, depth = 0) => {
  if (!value || depth > 5) return;
  if (Array.isArray(value)) {
    for (const item of value) collectStoragePaths(item, acc, depth + 1);
    return;
  }
  if (typeof value === "object") {
    const maybePath = value.storagePath;
    if (typeof maybePath === "string" && maybePath.trim()) {
      acc.add(maybePath.trim());
    }
    for (const v of Object.values(value)) collectStoragePaths(v, acc, depth + 1);
  }
};

const deleteStoragePathsBestEffort = async (paths, context = "storage") => {
  for (const path of paths) {
    try {
      await deleteObject(sRef(storage, path));
    } catch (err) {
      console.warn(`[${context}] storage delete failed for ${path}:`, err?.message || err);
    }
  }
};

const deleteQueryByChunks = async ({
  collectionRef,
  filters = [],
  label = "deleteQueryByChunks",
  beforeDelete = null,
}) => {
  let cursor = null;
  let total = 0;

  while (true) {
    const parts = [collectionRef, ...filters, orderBy("__name__")];
    if (cursor) parts.push(startAfter(cursor));
    parts.push(limit(DELETE_BATCH_SIZE));
    const snap = await getDocs(query(...parts));
    if (snap.empty) break;

    const batch = writeBatch(db);
    for (const docSnap of snap.docs) {
      if (beforeDelete) {
        try {
          await beforeDelete(docSnap);
        } catch (err) {
          console.error(
            `[${label}] beforeDelete error for ${docSnap.id}:`,
            err?.message || err
          );
        }
      }
      batch.delete(docSnap.ref);
    }

    try {
      await batch.commit();
    } catch (err) {
      console.error(`[${label}] batch commit error:`, err?.message || err);
    }

    total += snap.size;
    cursor = snap.docs[snap.docs.length - 1];
    if (snap.size < DELETE_BATCH_SIZE) break;
  }

  return total;
};

export const MODELE_CONFIG_SOURCE_PRIORITY = [
  "configURL",
  "configInline",
  "fields_meta",
  "docxMeta",
  "docx",
];

const hasKeys = (obj) =>
  obj && typeof obj === "object" && Object.keys(obj).length > 0;

export const resolveModeleConfigSource = (modele = {}) => {
  if (!modele || typeof modele !== "object") {
    return { kind: "none", label: "aucune" };
  }

  if (modele.configURL) {
    return { kind: "configURL", label: "fichier" };
  }
  if (modele.configInline) {
    return { kind: "configInline", label: "inline" };
  }
  if (hasKeys(modele.fields_meta)) {
    return { kind: "fields_meta", label: "inline" };
  }
  const docxMeta = modele.docxMeta || modele.docx_meta;
  if (docxMeta && Array.isArray(docxMeta.keys) && docxMeta.keys.length) {
    return { kind: "docxMeta", label: "docx" };
  }
  if (modele.fileURL) {
    return { kind: "docx", label: "docx" };
  }
  return { kind: "none", label: "aucune" };
};

// ---------- Preview helpers (owner) ----------
// Normalise une config inline minimale pour DynamicForm
export function normalizeConfigForPreview(raw) {
  if (!raw || typeof raw !== "object") return null;
  const cfg = JSON.parse(JSON.stringify(raw));

  // ✅ Patch 4: Si fields est un objet, on injecte la key dans chaque field
  if (cfg.fields && !Array.isArray(cfg.fields)) {
    Object.entries(cfg.fields).forEach(([k, v]) => {
      if (v && typeof v === "object") {
        v.key = v.key ?? v.id ?? v.name ?? k;
      }
    });
  }

  cfg.fields = cfg.fields || {};
  cfg.sections = Array.isArray(cfg.sections) ? cfg.sections : [];
  cfg.maps = cfg.maps || {};
  return cfg;
}

// Parse un JSON inline (objet ou stringifi��) sans lever
const parseConfigObject = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  return null;
};

export async function extractConfigSnapshotFromModele(modele = {}) {
  // 1) configInline
  const inline = parseConfigObject(modele?.configInline);
  if (inline) {
    return normalizeConfigForPreview(inline);
  }
  // 2) fields_meta -> structure minimale
  const fieldsMeta = parseConfigObject(modele?.fields_meta);
  if (fieldsMeta && typeof fieldsMeta === "object") {
    return normalizeConfigForPreview({
      fields: fieldsMeta,
      sections: [],
      maps: {},
    });
  }
  // 3) configPath (fichier JSON sur Storage)
  if (modele?.configPath) {
    try {
      const ab = await getBytes(sRef(storage, modele.configPath));
      const txt = new TextDecoder().decode(ab);
      const json = JSON.parse(txt);
      return normalizeConfigForPreview(json);
    } catch (_) { }
  }

  // 3) configURL -> fetch JSON
  if (modele?.configURL) {
    try {
      const res = await fetch(modele.configURL);
      if (res.ok) {
        const json = await res.json();
        return normalizeConfigForPreview(json);
      }
    } catch (_) { }
  }

  // 5) docxMeta/docx: pas de config exploitable
  return null;
}

// Renvoie la config à partir d'un item marketplace (préférée: configSnapshot)
export async function makeFormConfigFromItemForPreview(item) {
  if (item?.configSnapshot) {
    return normalizeConfigForPreview(item.configSnapshot);
  }
  // Pas de fallback Firestore ici pour ne pas violer les règles /users/{uid}
  // (l'admin n'a pas lecture des modèles privés des vendeurs).
  return null;
}

export async function createShareLinkForModele(
  uid,
  modeleId,
  { expiresInDays = 30, maxSubmissions = null } = {}
) {
  if (!uid) throw new Error("createShareLinkForModele: uid manquant");
  if (!modeleId) throw new Error("createShareLinkForModele: modeleId manquant");

  const callable = httpsCallable(functions, "createShareLink");
  const res = await callable({ uid, modeleId, expiresInDays, maxSubmissions });
  return res?.data || null; // { url, token, ... } selon ton backend
}

// Charge vite depuis cache local puis synchronise Firestore
export const loadAppSettingsHybrid = (uid, onLive) => {
  const appRef = doc(db, "users", uid, "settings", APP_SETTINGS_KEY);
  const CACHE_KEY = appSettingsCacheKey(uid);

  // 1) cache immédiat
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) onLive?.(JSON.parse(cached));
  } catch { }

  // 2) live Firestore (stale-while-revalidate)
  return onSnapshot(appRef, (snap) => {
    const data = snap.exists() ? snap.data() : null;
    if (data) localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    onLive?.(data);
  });
};
export const loadAppSettings = async (uid) => {
  const docRef = doc(db, "users", uid, "settings", APP_SETTINGS_KEY);
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
};

export const saveAppSettings = async (uid, patch) => {
  const docRef = doc(db, "users", uid, "settings", APP_SETTINGS_KEY);
  await setDoc(
    docRef,
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true }
  );
  // best-effort: mettre à jour le cache local pour le rendu instantané
  try {
    const CACHE_KEY = appSettingsCacheKey(uid);
    const prev = JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch { }
};
export const ensureAppSettings = async (uid) => {
  if (!uid) throw new Error("UID manquant");

  const settingsRef = doc(db, "users", uid, "settings", APP_SETTINGS_KEY);
  const snap = await getDoc(settingsRef);
  if (snap.exists()) return snap.data();

  // Anciennes sources (si elles existent encore)
  const dsRef = doc(db, "users", uid, "settings", "dossiers_settings");
  const rsRef = doc(db, "users", uid, "settings", "relations_settings");
  const [dsSnap, rsSnap] = await Promise.all([getDoc(dsRef), getDoc(rsRef)]);

  const dossiers = dsSnap.exists()
    ? (dsSnap.data() || {})
    : { main: { label: "Dossiers", icon: "fas fa-folder", defaultOpen: true }, types: [], fields: [] };

  const relations = rsSnap.exists()
    ? (rsSnap.data() || {})
    : { main: { label: "Relations", icon: "fas fa-address-book", defaultOpen: true }, types: [], fields: [] };

  const payload = {
    desc: "Réglages fusionnés",
    limits: { maxDossiers: 100, maxRelations: 100, maxDashboards: 50 },
    dossiers,
    relations,
    updatedAt: serverTimestamp(),
  };

  await setDoc(settingsRef, payload, { merge: true });
  return payload;
};


/** Utils de chemin par utilisateur */
const col = (uid, name) => collection(db, 'users', uid, name)
const settingsDoc = (uid, key) => doc(db, 'users', uid, 'settings', key)
// Dans data.js, remplacez la fonction updateModeleRecord par :
export const updateModeleRecord = async (uid, modeleId, data) => {
  try {
    const modeleRef = doc(db, 'users', uid, 'modeles', modeleId);
    const body = {
      ...data,
      updatedAt: serverTimestamp(),
    };
    if (data && data.fields_meta) {
      body.fields_meta_updatedAt = serverTimestamp();
      body.fields_meta_version = data.fields_meta_version || 1;
    }
    await updateDoc(modeleRef, body);
  } catch (error) {
    console.error("Erreur mise à jour modèle:", error);
    throw error;
  }
};
/** ---- Schémas (settings) ---- **/
export const saveEntitySchema = async (uid, entity, schema) => {
  return setDoc(settingsDoc(uid, `${entity}Schema`), { schema, updatedAt: serverTimestamp() })
}
export const loadEntitySchema = async (uid, entity) => {
  const snap = await getDoc(settingsDoc(uid, `${entity}Schema`))
  return snap.exists() ? (snap.data().schema || []) : null
}
export const loadDossiersSettings = async (uid) => {
  const app = (await loadAppSettings(uid)) || (await ensureAppSettings(uid));
  return app?.dossiers || { main: {}, types: [], fields: [] };
};
export const saveDossiersSettings = async (uid, data) => {
  return saveAppSettings(uid, { dossiers: data });
};

export const loadRelationsSettings = async (uid) => {
  const app = (await loadAppSettings(uid)) || (await ensureAppSettings(uid));
  return app?.relations || { main: {}, types: [], fields: [] };
};
export const saveRelationsSettings = async (uid, data) => {
  return saveAppSettings(uid, { relations: data });
};

/** ======================================================
 * Sauvegarde automatique des settings utilisateur
 * ====================================================== */

/**
 * Enregistre une configuration (settings) dans Firestore + LocalStorage
 * @param {string} uid - Identifiant Firebase de l'utilisateur
 * @param {string} key - Nom unique du réglage (ex: "dossiers_settings", "relations_settings")
 * @param {object} data - Données à sauvegarder (JSON)
 */
export const saveUserSetting = async (uid, key, data) => {
  if (!uid) throw new Error("UID manquant");
  const docRef = doc(db, "users", uid, "settings", key);
  await setDoc(
    docRef,
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  console.log(`[warn] Setting "${key}" enregistré dans Firestore`);
};


/**
 * Charge une configuration utilisateur (settings)
 * @param {string} uid - Identifiant Firebase
 * @param {string} key - Nom du réglage (ex: "dossiers_settings")
 * @returns {object} data - Données des settings
 */
export const loadUserSetting = async (uid, key) => {
  const docRef = doc(db, "users", uid, "settings", key);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    const data = snap.data();
    console.log(`[info] Firestore + ${key}`, data);
    return data;
  }
  console.warn(`[warn] Aucun doc Firestore pour ${key}`);
  return null;
};

/** =========================
 *  PROFILE (wrappers simples)
 *  =========================*/
export const loadUserProfile = async (uid) => {
  try {
    const snap = await getDoc(doc(db, "users", uid, "settings", "profile"));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.warn("loadUserProfile:", e?.message || e);
    return {};
  }
};
export const saveUserProfile = async (uid, data) => {
  return saveUserSetting(uid, "profile", data);
};

/** Compteurs rapides (évite les grosses lectures) */
export const getUserCounts = async (uid) => {
  if (!uid) return { docs: 0, modeles: 0 };
  const [docsSnap, modelesSnap] = await Promise.all([
    getCountFromServer(collection(db, "users", uid, "documents")),
    getCountFromServer(collection(db, "users", uid, "modeles")),
  ]);
  return {
    docs: docsSnap.data().count || 0,
    modeles: modelesSnap.data().count || 0,
  };
};

/** =========================
 *  MARKETPLACE (draft / publish)
 *  =========================*/
/**
 * Enregistre un brouillon de fiche marketplace.
 * Chemin: users/{uid}/marketplace_drafts/{draftId}
 */
export const saveMarketplaceDraft = async (uid, draft) => {
  if (!uid) throw new Error("UID manquant");
  const colRef = collection(db, "users", uid, "marketplace_drafts");
  const docRef = draft?.id ? doc(colRef, draft.id) : doc(colRef);
  await setDoc(
    docRef,
    {
      ownerUid: uid,
      ...draft,
      status: "draft",
      updatedAt: serverTimestamp(),
      createdAt: draft?.createdAt || serverTimestamp(),
    },
    { merge: true }
  );
  return { id: docRef.id };
};

/**
 * Crée un item public en attente de modération.
 * Chemin: marketplace_items/{itemId}
 */
export const publishMarketplaceItem = async (uid, payload) => {
  if (!uid) throw new Error("UID manquant");
  const itemRef = doc(collection(db, "marketplace_items"));
  await setDoc(itemRef, {
    ownerUid: uid,
    ...payload,
    status: "pending_review",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: itemRef.id };
};

export const listenPendingMarketplaceItems = (cb) => {
  const qy = query(
    collection(db, "marketplace_items"),
    where("status", "==", "pending_review"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(
    qy,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error("listenPendingMarketplaceItems:", err?.message || err);
      cb([]);
    }
  );
};

/** =========================
 *  MARKETPLACE – Helpers gestion
 *  =========================*/
export const listenMarketplaceDrafts = (uid, cb) => {
  const qy = query(
    collection(db, "users", uid, "marketplace_drafts"),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(
    qy,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error("listenMarketplaceDrafts:", err?.message || err);
      cb([]);
    }
  );
};

export const listenMarketplaceItemsByStatus = ({ status, ownerUid }, cb) => {
  const parts = [collection(db, "marketplace_items")];
  if (ownerUid) parts.push(where("ownerUid", "==", ownerUid));
  if (status) parts.push(where("status", "==", status));
  const qy = query(...parts); // no server orderBy -> no composite index required

  const toMs = (v) => {
    if (!v) return 0;
    if (typeof v.toDate === "function") return v.toDate().getTime();
    if (typeof v.seconds === "number") return v.seconds * 1000;
    if (typeof v === "number") return v;
    const p = Date.parse(v);
    return Number.isNaN(p) ? 0 : p;
  };

  return onSnapshot(
    qy,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort(
        (a, b) =>
          (toMs(b.updatedAt) || toMs(b.createdAt)) -
          (toMs(a.updatedAt) || toMs(a.createdAt))
      );
      cb(rows);
    },
    (err) => {
      console.error("listenMarketplaceItemsByStatus:", err?.message || err);
      cb([]);
    }
  );
};

export const updateMarketplaceItemStatus = async (itemId, status, extra = {}) => {
  const ref = doc(db, "marketplace_items", itemId);
  const body = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (status === "published") body.publishedAt = serverTimestamp();
  if (status === "rejected") body.rejectedReason = extra?.rejectedReason || "rejeté";
  if (status === "pending_review") body.rejectedReason = null;
  await setDoc(ref, body, { merge: true });
};

// ✅ Admin: mise à jour partielle des champs d’un item marketplace
export const updateMarketplaceItemFields = async (itemId, patch = {}) => {
  const ref = doc(db, "marketplace_items", itemId);
  const body = { ...patch, updatedAt: serverTimestamp() };
  await setDoc(ref, body, { merge: true });
};

// Enregistre qu'un utilisateur a importé/téléchargé un item
export const recordMarketplaceImport = async (uid, itemId) => {
  if (!uid || !itemId) return;
  const now = serverTimestamp();
  const importerRef = doc(db, "marketplace_items", itemId, "importers", uid);
  const userImportRef = doc(db, "users", uid, "marketplace_imports", itemId);
  try {
    // Toujours persister le marqueur côté utilisateur (moins soumis aux règles publiques)
    await setDoc(userImportRef, { itemId, importedAt: now }, { merge: true });
    // Best-effort côté marketplace item (peut échouer suivant les règles)
    try {
      await setDoc(importerRef, { uid, itemId, importedAt: now }, { merge: true });
    } catch (err) {
      console.warn("[marketplace] importerRef write failed (ignored):", err?.message || err);
    }
  } catch (err) {
    console.warn("[marketplace] recordMarketplaceImport failed:", err?.message || err);
  }
};

export const hasUserImportedMarketplaceItem = async (uid, itemId) => {
  if (!uid || !itemId) return false;
  try {
    const ref2 = doc(db, "users", uid, "marketplace_imports", itemId);
    const snap2 = await getDoc(ref2);
    if (snap2.exists()) return true;
    const ref1 = doc(db, "marketplace_items", itemId, "importers", uid);
    const snap1 = await getDoc(ref1);
    return snap1.exists();
  } catch (err) {
    console.warn("[marketplace] hasUserImported check failed:", err?.message || err);
    return false;
  }
};

const recomputeMarketplaceRatings = async (itemId) => {
  const reviewsCol = collection(db, "marketplace_items", itemId, "reviews");
  try {
    const snap = await getDocs(reviewsCol);
    const docs = snap.docs.map((d) => d.data() || {});
    const count = docs.length;
    let sum = 0;
    docs.forEach((r) => {
      const n = Number(r.rating);
      if (Number.isFinite(n)) sum += n;
    });
    const ratingAvg = count > 0 ? sum / count : null;
    await setDoc(
      doc(db, "marketplace_items", itemId),
      {
        ratingAvg: ratingAvg != null ? Number(ratingAvg.toFixed(2)) : null,
        ratingCount: count,
        reviewsCount: count,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn("[marketplace] recompute ratings failed:", err?.message || err);
  }
};

export const addMarketplaceReview = async ({
  itemId,
  uid,
  rating,
  comment = "",
  userDisplayName = "",
}) => {
  if (!itemId || !uid) throw new Error("itemId/uid manquants");
  const reviewsCol = collection(db, "marketplace_items", itemId, "reviews");
  const ref = doc(reviewsCol, uid);
  const safeRating = Math.max(1, Math.min(5, Number(rating) || 0));

  let createdAt = serverTimestamp();
  try {
    const existing = await getDoc(ref);
    if (existing.exists() && existing.data()?.createdAt) {
      createdAt = existing.data().createdAt;
    }
  } catch { }

  await setDoc(
    ref,
    {
      itemId,
      userId: uid,
      rating: safeRating,
      comment: (comment || "").trim(),
      userDisplayName: userDisplayName || "",
      updatedAt: serverTimestamp(),
      createdAt,
    },
    { merge: true }
  );

  await recomputeMarketplaceRatings(itemId);
};

export const listenMarketplaceReviews = (itemId, cb) => {
  if (!itemId) return () => { };
  const reviewsCol = collection(db, "marketplace_items", itemId, "reviews");
  const qy = query(reviewsCol, orderBy("createdAt", "desc"));
  return onSnapshot(
    qy,
    (snap) => {
      const out = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(out);
    },
    (err) => {
      console.warn("[marketplace] listen reviews failed:", err?.message || err);
      cb([]);
    }
  );
};

// Incrémente le compteur d'import/téléchargement d'un item marketplace
export const incrementMarketplaceDownloadCount = async (itemId) => {
  if (!itemId) return;
  const ref = doc(db, "marketplace_items", itemId);
  try {
    await updateDoc(ref, {
      importsCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("[marketplace] increment downloads failed:", err?.message || err);
  }
};

// Supprime un item marketplace + assets Storage sous marketplace_items/{id}
export const deleteMarketplaceItem = async (itemId) => {
  if (!itemId) throw new Error("itemId manquant");

  const ref = doc(db, "marketplace_items", itemId);
  const paths = new Set();

  // Helper interne pour collecter des chemins Storage sous "marketplace_items/"
  const collectPath = (val) => {
    if (!val) return;
    if (Array.isArray(val)) {
      val.forEach(collectPath);
      return;
    }
    if (typeof val === "string") {
      const p = val.trim();
      // On ne touche que les fichiers de release du marketplace
      if (p && p.startsWith("marketplace_items/")) {
        paths.add(p);
      }
      return;
    }
    if (typeof val === "object") {
      Object.values(val).forEach(collectPath);
    }
  };

  try {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data() || {};

      // Assets de la release (créés par approveMarketplaceItemWithRelease)
      if (data.release) {
        collectPath(data.release.thumbPath);
        collectPath(data.release.galleryPaths);
        collectPath(data.release.docxPath);
        collectPath(data.release.configPath);
      }

      // (Optionnel) Si jamais tu stockes aussi des chemins directs sous marketplace_items/
      collectPath(data.thumbPath);
      collectPath(data.galleryPaths);
      collectPath(data.templatePath);
      collectPath(data.configPath);
    }
  } catch (err) {
    console.warn(
      "[marketplace_delete] lecture item avant delete échouée:",
      err?.message || err
    );
  }

  // Suppression best-effort des fichiers Storage
  if (paths.size) {
    await deleteStoragePathsBestEffort(paths, "marketplace_delete");
  }

  // Suppression du document Firestore
  await deleteDoc(ref);
};

export const deleteMarketplaceDraft = async (uid, draftId) => {
  await deleteDoc(doc(db, "users", uid, "marketplace_drafts", draftId));
};

export const checkStorageUserWrite = async (uid) => {
  if (!uid) throw new Error("uid manquant pour checkStorageUserWrite");
  const path = `users/${uid}/__permcheck__/${Date.now()}.txt`;
  const blob = new Blob(["ok"], { type: "text/plain" });
  await uploadBytesResumable(sRef(storage, path), blob, {
    contentType: "text/plain",
  });
  try {
    await deleteObject(sRef(storage, path));
  } catch { }
  return true;
};

export const checkStorageAdminWrite = async () => {
  try {
    const { auth } = await import("../firebase/firebase");
    await auth.currentUser?.getIdToken(true);
  } catch { }
  const path = `marketplace_items/__permcheck__/${Date.now()}.txt`;
  const blob = new Blob(["ok"], { type: "text/plain" });
  const effBucket = sRef(storage, "/").bucket;
  console.log("[permcheck] bucket =", effBucket, "path =", path);
  await uploadBytesResumable(sRef(storage, path), blob, {
    contentType: "text/plain",
  });
  try {
    await deleteObject(sRef(storage, path));
  } catch { }
  return true;
};

// ===== MARKETPLACE – Release helpers (admin only) =====
// Copie un objet Storage -> nouveau chemin (client-side)
const copyStorageFile = async (srcPath, destPath) => {
  if (!srcPath || !destPath) return null;
  const url = await getDownloadURL(sRef(storage, srcPath));
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch blob failed for ${srcPath}`);
  const blob = await res.blob();
  const meta = blob.type ? { contentType: blob.type } : undefined;
  await uploadBytesResumable(sRef(storage, destPath), blob, meta);
  return destPath;
};

/**
 * Approve + release assets:
 * - crée marketplace_items/{itemId}/
 * - copie thumbPath / galleryPaths / templatePath si présents
 * - met à jour le doc avec {status:"published", release:{...}}
 */
export const approveMarketplaceItemWithRelease = async (item) => {
  // item = {id, ownerUid, thumbPath?, galleryPaths?, templatePath? ...}
  if (!item?.id) throw new Error("item.id manquant");
  const releaseFolder = `marketplace_items/${item.id}`;
  const out = { folder: releaseFolder };

  // Copie vignette
  if (item.thumbPath) {
    const ext = item.thumbPath.split(".").pop() || "bin";
    out.thumbPath = await copyStorageFile(
      item.thumbPath,
      `${releaseFolder}/thumb.${ext}`
    );
  }

  // Copie galerie
  if (Array.isArray(item.galleryPaths) && item.galleryPaths.length) {
    const arr = [];
    for (let i = 0; i < item.galleryPaths.length; i++) {
      const src = item.galleryPaths[i];
      const ext = (src || "").split(".").pop() || "bin";
      arr.push(await copyStorageFile(src, `${releaseFolder}/gallery-${i + 1}.${ext}`));
    }
    out.galleryPaths = arr;
  }

  // Copie fichier modèle si fourni (champ `templatePath`)
  if (item.templatePath) {
    const ext = (item.templatePath || "").split(".").pop() || "docx";
    out.docxPath = await copyStorageFile(
      item.templatePath,
      `${releaseFolder}/template.${ext}`
    );
  }

  // ---- NEW: embarquer la config dans la release ----
  if (item.configSnapshot && typeof item.configSnapshot === "object") {
    out.configSnapshot = item.configSnapshot;
  }
  if (item.configPath) {
    out.configPath = await copyStorageFile(
      item.configPath,
      `${releaseFolder}/config.json`
    );
  }

  // Mise à jour du doc
  const refDoc = doc(db, "marketplace_items", item.id);
  const releaseUrls = {};
  try {
    if (out.thumbPath) {
      releaseUrls.thumbUrl = await getDownloadURL(sRef(storage, out.thumbPath));
    }
    if (Array.isArray(out.galleryPaths) && out.galleryPaths.length) {
      releaseUrls.galleryUrls = await Promise.all(
        out.galleryPaths.map((p) => getDownloadURL(sRef(storage, p)))
      );
    }
    if (out.docxPath) {
      releaseUrls.docxUrl = await getDownloadURL(sRef(storage, out.docxPath));
    }
    if (out.configPath) {
      releaseUrls.configUrl = await getDownloadURL(sRef(storage, out.configPath));
    }
  } catch (_) { }
  await setDoc(
    refDoc,
    {
      status: "published",
      updatedAt: serverTimestamp(),
      publishedAt: serverTimestamp(),
      release: out,
      releaseUrls,
    },
    { merge: true }
  );
};

/** ---- Clients ---- **/
export const listenClients = (uid, cb) => {
  const q = query(col(uid, 'clients'), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => {
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    cb(out)
  }, (err) => {
    console.error('listenClients error:', err?.message || err);
    cb([]); // évite écran vide bloqué
  })
}
/** ---- Fournisseurs (mêmes helpers) ---- **/
export const listenFournisseurs = (uid, cb) => {
  const q = query(col(uid, 'fournisseurs'), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => {
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    cb(out)
  }, (err) => {
    console.error('listenFournisseurs error:', err?.message || err);
    cb([]);
  })
}
export const addFournisseur = async (uid, payload) => {
  return addDoc(col(uid, 'fournisseurs'), { ...payload, createdAt: serverTimestamp() })
}

export const addClient = async (uid, payload) => {
  return addDoc(col(uid, 'clients'), {
    ...payload,
    createdAt: serverTimestamp(),
  })
}

/** ---- Documents ---- **/
export const listenDocuments = (uid, cb) => {
  const q = query(col(uid, 'documents'), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => {
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    cb(out)
  }, (err) => {
    console.error('listenDocuments error:', err?.message || err);
    cb([]);
  })
}

export const addDocumentMeta = async (uid, payload) => {
  const safePayload = {
    ...payload,
    createdAt: serverTimestamp(),
    dossierKey: payload?.dossierKey ?? payload?.dossier ?? null,
  }

  return addDoc(col(uid, 'documents'), safePayload)
}
export const deleteDocumentMeta = async (uid, docId) => {
  if (!uid || !docId) return;
  await deleteDoc(doc(db, 'users', uid, 'documents', docId));
};


// [info] Temps réel limité: derniers modèles, ordonnés par updatedAt desc (fallback: createdAt si besoin)
export const listenModeles = (uid, cb, opts = {}) => {
  if (!uid) return () => { };
  const { pageSize = PAGE_SIZES.modeles } = opts;

  // [warn] Assurez-vous que updatedAt est bien présent sur tous les docs (c'est déjà le cas via add/update)
  let qy = query(col(uid, 'modeles'), orderBy('updatedAt', 'desc'), limit(pageSize));

  return onSnapshot(qy, (snap) => {
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(out);
  }, (err) => {
    console.error('listenModeles error:', err?.message || err);
    cb([]);
  });
};

// *... Hybride: rend le cache immédiatement, puis fetch ponctuel pour minimiser les lectures
export const listenModelesHybrid = async (uid, cb, opts = {}) => {
  if (!uid) return () => { };
  const { pageSize = PAGE_SIZES.modeles } = opts;
  const CACHE_KEY = modelesCacheKey(uid);

  // 1) cache immédiat
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) cb(JSON.parse(cached));
  } catch { }

  // 2) fetch ponctuel limité & trié (pas de onSnapshot pour minimiser les lectures)
  try {
    const qy = query(col(uid, 'modeles'), orderBy('updatedAt', 'desc'), limit(pageSize));
    const snap = await getDocs(qy);
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(out)); } catch { }
    cb(out);
  } catch (err) {
    console.error('listenModelesHybrid error:', err?.message || err);
    // cache déjà rendu si présent
  }

  // Retourne une fonction vide car pas de unsubscribe nécessaire
  return () => { };
};

export const addModeleRecord = async (uid, payload) => {
  const body = {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (payload && payload.fields_meta) {
    body.fields_meta_updatedAt = serverTimestamp();
    body.fields_meta_version = payload.fields_meta_version || 1;
  }
  const docRef = await addDoc(col(uid, 'modeles'), body);
  return { id: docRef.id, ...body };
};
/** Assainir le nom de fichier */
const sanitizeFileName = (name = "file") =>
  name.replace(/[^\w.\-]+/g, "_").slice(0, 180); // évite les caractères bizarres et noms trop longs
/** ---- Upload Storage sécurisé (aligné avec les règles Firebase) ---- **/
/** ---- Upload Storage robuste (sans storageBridge) ---- **/
export const uploadToStorage = async ({
  uid,
  file,
  folder = "uploads",
  path,
  onProgress,
  contentType,
}) => {
  if (!uid) throw new Error("[warn] UID manquant (utilisateur non connecté)");
  if (!file) throw new Error("[warn] Aucun fichier à téléverser");

  // On range les fichiers sous users/{uid}/... pour coller à des règles Storage par utilisateur
  const ts = Date.now();
  const safeName = sanitizeFileName(file.name || "file.bin");
  const normalizedPath = path
    ? String(path).replace(/^\/+|\/+$/g, "")
    : `${folder}/${ts}__${safeName}`;
  const storagePath = `users/${uid}/${normalizedPath}`;

  // Métadonnées MIME facultatives (recommandé pour .docx/.json/.xlsx/.csv)
  const metadata = {};
  if (contentType) metadata.contentType = contentType;

  // Upload avec progression
  const storageRef = sRef(storage, storagePath);
  const task = uploadBytesResumable(storageRef, file, metadata);

  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          const p = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress(p);
        }
      },
      (err) => {
        reject(err);
      },
      async () => {
        try {
          const fileURL = await getDownloadURL(task.snapshot.ref);
          // Progress à 100% assuré
          onProgress?.(100);
          resolve({ storagePath, fileURL, size: file.size });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};
/** ================================
 * Records (brouillons + finals)
 * Collection: users/{uid}/records
 * ================================ */
// ----- Dossiers : enregistrements générés -----
// ---------- ÉCRITURE TOUJOURS dans "dossier_records"
export const addDossierRecord = async (uid, payload) => {
  const colRef = collection(db, 'users', uid, 'dossier_records');
  const docRef = await addDoc(colRef, {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id };
};

export const updateDossierRecord = async (uid, id, payload) => {
  const refDoc = doc(db, 'users', uid, 'dossier_records', id);
  await updateDoc(refDoc, { ...payload, updatedAt: serverTimestamp() });
};

// [info] Temps réel borné + tri cohérent; option fenêtre temporelle
export function listenDossierRecords(uid, opts, onRows) {
  if (!uid) return () => { };
  const {
    dossierKey = null,
    statut = null,
    windowDays = null,             // ex: 30 +' derniers 30 jours
    pageSize = PAGE_SIZES.dossierRecords,
  } = opts || {};

  const colRef = collection(db, "users", uid, "dossier_records");

  const filters = [];
  if (dossierKey) filters.push(where("dossierKey", "==", dossierKey));
  if (statut) filters.push(where("statut", "==", statut));
  // Optionnel: borne temporelle côté serveur (réduit les lectures)
  if (windowDays && Number(windowDays) > 0) {
    const since = new Date(Date.now() - Number(windowDays) * 86400000);
    filters.push(where("updatedAt", ">=", Timestamp.fromDate(since)));
  }

  let qy = query(colRef, ...filters, orderBy("updatedAt", "desc"), limit(pageSize));

  return onSnapshot(qy, (snap) => {
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onRows(rows);
  }, (err) => {
    console.error('listenDossierRecords error:', err?.message || err);
    onRows([]);
  });
}

// *... Hybride: rend le cache immédiatement puis fetch ponctuel pour minimiser les lectures

let hasLoggedMissingIndex = false;
const isMissingIndexError = (err) => err?.code === "failed-precondition" && /index/i.test(err?.message || "");

const runQueryWithIndexFallback = async (primaryBuilder, fallbackBuilder) => {
  try {
    const snap = await primaryBuilder();
    return { snap, fromFallback: false };
  } catch (err) {
    if (isMissingIndexError(err) && typeof fallbackBuilder === "function") {
      if (!hasLoggedMissingIndex) {
        hasLoggedMissingIndex = true;
        console.warn('[data] Firestore index missing, fallback to client-side merge:', err?.message || err);
      }
      const snap = await fallbackBuilder();
      return { snap, fromFallback: true, originalError: err };
    }
    throw err;
  }
};

const resolveRecordTimestamp = (row) => {
  const candidates = [
    row?.updatedAt,
    row?.updated_at,
    row?.createdAt,
    row?.created_at,
    row?.timestamp,
    row?.ts,
    row?._ts,
    row?.date,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (typeof candidate === "object") {
      if (typeof candidate.toDate === "function") {
        return candidate.toDate().getTime();
      }
      if (typeof candidate.seconds === "number") {
        return candidate.seconds * 1000 + Math.floor((candidate.nanoseconds || 0) / 1e6);
      }
    }
    if (typeof candidate === "number" && !Number.isNaN(candidate)) return candidate;
    if (typeof candidate === "string") {
      const parsed = Date.parse(candidate);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return 0;
};

const mergeDossierRecordSnapshots = (snaps, { limitTo, includeSnap } = {}) => {
  const rows = [];
  const seen = new Set();

  snaps.forEach((snap) => {
    if (!snap) return;
    snap.docs.forEach((docSnap) => {
      if (seen.has(docSnap.id)) return;
      seen.add(docSnap.id);
      const base = { id: docSnap.id, ...docSnap.data() };
      if (includeSnap) {
        rows.push({ ...base, __snap: docSnap });
      } else {
        rows.push(base);
      }
    });
  });

  rows.sort((a, b) => resolveRecordTimestamp(b) - resolveRecordTimestamp(a));

  if (limitTo && rows.length > limitTo) {
    return rows.slice(0, limitTo);
  }

  return rows;
};

const recordMatchesFilters = (row, { dossierKey, statut, windowDays }) => {
  if (dossierKey) {
    const rowKey = row?.dossierKey ?? row?.dossier ?? row?.values?.dossierKey ?? "";
    if (rowKey !== dossierKey) return false;
  }
  if (statut && row?.statut !== statut) return false;
  if (windowDays && Number(windowDays) > 0) {
    const cutoff = Date.now() - Number(windowDays) * 86400000;
    if (resolveRecordTimestamp(row) < cutoff) return false;
  }
  return true;
};

export async function listenDossierRecordsHybrid(uid, opts, onRows) {
  if (!uid) return () => { };
  const {
    dossierKey = "",
    statut = "",
    windowDays = null,
    pageSize = PAGE_SIZES.dossierRecords,
  } = opts || {};

  const CK = dossierRecordsCacheKey(uid, { dossierKey, statut });

  // 1) cache immédiat
  try {
    const cached = localStorage.getItem(CK);
    if (cached) onRows(JSON.parse(cached));
  } catch { }

  const colRef = collection(db, "users", uid, "dossier_records");
  const FALLBACK_LIMIT = Math.max(pageSize, 60);

  const baseFilters = [];
  if (statut) baseFilters.push(where("statut", "==", statut));
  if (windowDays && Number(windowDays) > 0) {
    const since = new Date(Date.now() - Number(windowDays) * 86400000);
    baseFilters.push(where("updatedAt", ">=", Timestamp.fromDate(since)));
  }

  const buildFallbackClauses = (field) => {
    const clauses = [];
    if (field) clauses.push(where(field, "==", dossierKey));
    if (statut) clauses.push(where("statut", "==", statut));
    return clauses;
  };

  // 2) fetch ponctuel limité & trié (pas de onSnapshot pour minimiser les lectures)
  try {
    const snapshots = [];

    if (dossierKey) {
      const orderedMain = query(
        colRef,
        where("dossierKey", "==", dossierKey),
        ...baseFilters,
        orderBy("updatedAt", "desc"),
        limit(pageSize)
      );
      const { snap: mainSnap } = await runQueryWithIndexFallback(
        () => getDocs(orderedMain),
        () => getDocs(query(colRef, ...buildFallbackClauses("dossierKey"), limit(FALLBACK_LIMIT)))
      );
      snapshots.push(mainSnap);

      const orderedLegacy = query(
        colRef,
        where("dossier", "==", dossierKey),
        ...baseFilters,
        orderBy("updatedAt", "desc"),
        limit(pageSize)
      );
      const { snap: legacySnap } = await runQueryWithIndexFallback(
        () => getDocs(orderedLegacy),
        () => getDocs(query(colRef, ...buildFallbackClauses("dossier"), limit(FALLBACK_LIMIT)))
      );
      snapshots.push(legacySnap);
    } else {
      const ordered = query(
        colRef,
        ...baseFilters,
        orderBy("updatedAt", "desc"),
        limit(pageSize)
      );
      const { snap } = await runQueryWithIndexFallback(
        () => getDocs(ordered),
        () => getDocs(query(colRef, ...(statut ? [where("statut", "==", statut)] : []), limit(FALLBACK_LIMIT)))
      );
      snapshots.push(snap);
    }

    const rows = mergeDossierRecordSnapshots(snapshots, { limitTo: pageSize }).filter((row) =>
      recordMatchesFilters(row, { dossierKey, statut, windowDays })
    );
    try {
      localStorage.setItem(CK, JSON.stringify(rows));
    } catch { }
    onRows(rows);
  } catch (err) {
    console.error('listenDossierRecordsHybrid error:', err?.message || err);
  }

  // Retourne une fonction vide car pas de unsubscribe nécessaire
  return () => { };
}


export const deleteDossierRecord = async (uid, { id, storagePath }) => {
  try {
    if (storagePath) await deleteObject(sRef(storage, storagePath));
  } catch (e) {
    console.warn('Storage delete warning:', e?.message || e);
  }
  await deleteDoc(doc(db, 'users', uid, 'dossier_records', id));
};

export const cascadeDeleteDossierType = async (uid, dossierKey) => {
  if (!uid || !dossierKey) return { deletedRecords: 0 };
  const label = `cascadeDeleteDossierType:${dossierKey}`;

  const collectionRef = collection(db, "users", uid, "dossier_records");
  const deletedRecords = await deleteQueryByChunks({
    collectionRef,
    filters: [where("dossierKey", "==", dossierKey)],
    label,
    beforeDelete: async (docSnap) => {
      const data = docSnap.data() || {};
      const paths = new Set();
      collectStoragePaths(data, paths);
      if (paths.size) {
        await deleteStoragePathsBestEffort(paths, label);
      }
    },
  });

  const fieldsColRef = collection(db, "users", uid, "dossier_fields");
  const deletedFieldsByKey = await deleteQueryByChunks({
    collectionRef: fieldsColRef,
    filters: [where("dossierKey", "==", dossierKey)],
    label: `${label}:fields:dossierKey`,
  });
  const deletedFieldsByLegacy = await deleteQueryByChunks({
    collectionRef: fieldsColRef,
    filters: [where("dossier", "==", dossierKey)],
    label: `${label}:fields:dossier`,
  });
  const deletedFieldsByType = await deleteQueryByChunks({
    collectionRef: fieldsColRef,
    filters: [where("type", "==", dossierKey)],
    label: `${label}:fields:type`,
  });

  let deletedFieldsDirect = 0;
  try {
    await deleteDoc(doc(db, "users", uid, "dossier_fields", dossierKey));
    deletedFieldsDirect = 1;
  } catch (err) {
    if (err?.code !== "not-found") {
      console.warn(`[${label}] failed to delete dossier_fields doc:`, err?.message || err);
    }
  }

  try {
    const keysToClear = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(`${DOS_REC_CACHE_KEY}:${uid}:`)) keysToClear.push(k);
    }
    keysToClear.forEach((k) => localStorage.removeItem(k));
  } catch { }

  return {
    deletedRecords,
    deletedFields:
      deletedFieldsByKey + deletedFieldsByLegacy + deletedFieldsByType + deletedFieldsDirect,
  };
};
// *... Normaliser une entrée field (clé, label, type)
const normField = (r = {}) => {
  const key = String(r.key || '').trim();
  if (!key) return null;
  return {
    key,
    label: String(r.label || r.name || key).trim(),
    type: String(r.type || 'text').toLowerCase().trim(),
    required: !!r.required,
    options: Array.isArray(r.options)
      ? r.options
      : (r.options ? String(r.options).split(',').map(s => s.trim()).filter(Boolean) : []),
  };
};

// *... Appliquer un delta d'ajout/suppression sur le registre d'un dossier
// *... Appliquer un delta d'ajout/suppression sur le registre d'un dossier (version transactionnelle)
const applyDeltaToDossierFields = async (uid, dossierKey, { add = {}, remove = {} }) => {
  if (!uid || !dossierKey) return;

  const refDoc = doc(db, 'users', uid, 'dossier_fields', dossierKey);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(refDoc);
    const current = snap.exists() ? (snap.data() || {}) : {};
    const fields = { ...(current.fields || {}) };

    // -- Suppressions / décrément
    for (const k of Object.keys(remove)) {
      const cur = fields[k];
      if (!cur) continue;
      const nextCount = Math.max(0, (cur.usageCount || 0) - 1);
      if (nextCount === 0) {
        delete fields[k];
      } else {
        fields[k] = { ...cur, usageCount: nextCount };
      }
    }

    // -- Ajouts / incrément
    for (const k of Object.keys(add)) {
      const f = normField(add[k]); if (!f) continue;
      const cur = fields[k];
      if (!cur) {
        fields[k] = { key: f.key, label: f.label, type: f.type, usageCount: 1 };
      } else {
        fields[k] = { ...cur, usageCount: (cur.usageCount || 0) + 1 };
      }
    }

    tx.set(refDoc, { dossierKey, fields, updatedAt: serverTimestamp() }, { merge: true });
  });
};


// *... Calculer le diff entre ancien et nouveau fields_meta (map par key)
const diffFieldsMeta = (oldMap = {}, newMap = {}) => {
  const add = {};
  const remove = {};
  const oKeys = new Set(Object.keys(oldMap || {}));
  const nKeys = new Set(Object.keys(newMap || {}));

  // ajoutés
  for (const k of nKeys) if (!oKeys.has(k)) add[k] = newMap[k];
  // retirés
  for (const k of oKeys) if (!nKeys.has(k)) remove[k] = oldMap[k];

  return { add, remove };
};

/**
 * *... Réconcilier le registre par dossier quand un modèle change
 * - Changement de dossier: retrait de l'ancien, ajout au nouveau
 * - Sinon: diff add/remove dans le même dossier
 */
export const reconcileDossierFieldsForModele = async (uid, {
  oldDossierKey = null,
  newDossierKey = null,
  oldFields = {},
  newFields = {},
}) => {
  if (!uid || !newDossierKey) return;

  if (oldDossierKey && oldDossierKey !== newDossierKey) {
    // 1) retirer totalement l'impact de l'ancien dossier
    const allOld = { ...oldFields };
    await applyDeltaToDossierFields(uid, oldDossierKey, { add: {}, remove: allOld });

    // 2) ajouter totalement l'impact au nouveau dossier
    const allNew = { ...newFields };
    await applyDeltaToDossierFields(uid, newDossierKey, { add: allNew, remove: {} });
    return;
  }

  // même dossier: calcul diff
  const { add, remove } = diffFieldsMeta(oldFields, newFields);
  if (Object.keys(add).length || Object.keys(remove).length) {
    await applyDeltaToDossierFields(uid, newDossierKey, { add, remove });
  }
};

/** ---- Suppression d'un modèle (doc + fichiers Storage) + mise à jour dossier_fields ---- **/
export const deleteModele = async (uid, { id, storagePath, configPath }) => {
  if (!uid || !id) throw new Error("uid/id manquants");
  const isUserScopedPath = (p) => {
    if (!p || typeof p !== "string") return false;
    const trimmed = p.trim();
    return trimmed.startsWith(`users/${uid}/`);
  };

  // 0) Lire le doc avant suppression pour récupérer dossier + fields_meta
  let oldDossierKey = null;
  let oldFieldsMeta = {};
  try {
    const snap = await getDoc(doc(db, 'users', uid, 'modeles', id));
    if (snap.exists()) {
      const data = snap.data() || {};
      oldDossierKey = data.dossier || null;
      oldFieldsMeta = data.fields_meta || {};
    }
  } catch (e) {
    console.warn('deleteModele: lecture doc avant suppression impossible:', e?.message || e);
  }

  // 1) Supprimer les fichiers Storage (best-effort)
  try {
    if (isUserScopedPath(storagePath)) {
      await deleteObject(sRef(storage, storagePath));
    }
    if (isUserScopedPath(configPath)) {
      await deleteObject(sRef(storage, configPath));
    }
  } catch (e) {
    console.warn('Storage delete warning:', e?.message || e);
  }

  // 2) Supprimer le document Firestore du modèle
  await deleteDoc(doc(db, 'users', uid, 'modeles', id));

  // 3) Mettre à jour le registre par dossier (retirer l'empreinte des champs de ce modèle)
  try {
    if (oldDossierKey) {
      await reconcileDossierFieldsForModele(uid, {
        oldDossierKey,
        newDossierKey: oldDossierKey, // même dossier, mais on passe newFields = {}
        oldFields: oldFieldsMeta,
        newFields: {}
      });
    }
  } catch (e) {
    console.warn('deleteModele: reconcile dossier_fields warning:', e?.message || e);
  }
  // 4) [info] Rebuild global de sécurité (nettoie les reliquats historiques, ex: d1 resté à 1)
  // 4) [info] Rebuild global désactivé par défaut (trop coûteux)
  try {
    if (REBUILD_ON_DELETE === true) {
      await rebuildAllDossierFields(uid);
    }
  } catch (e) {
    console.warn('deleteModele: rebuildAllDossierFields warning:', e?.message || e);
  }
};
/**
 * [info] Recalculer entièrement le registry d'un dossier à partir des modèles restants.
 * Écrit users/{uid}/dossier_fields/{dossierKey} de façon idempotente.
 */
export const rebuildDossierFields = async (uid, dossierKey) => {
  if (!uid || !dossierKey) return;

  // 1) Lire tous les modèles du dossier
  const colRef = collection(db, 'users', uid, 'modeles');
  const snap = await getDocs(query(colRef, where('dossier', '==', dossierKey)));

  // 2) Agréger fields_meta -> { key -> { key,label,type,usageCount } }
  const agg = {}; // key -> { key,label,type,count }
  snap.forEach(d => {
    const fm = d.data()?.fields_meta || {};
    for (const k of Object.keys(fm)) {
      const r = fm[k] || {};
      if (!r?.key) continue;
      if (!agg[k]) {
        agg[k] = { key: r.key, label: String(r.label || r.key), type: String(r.type || 'text'), count: 0 };
      }
      agg[k].count += 1;
    }
  });

  // 3) Construire payload "fields"
  const fields = {};
  for (const k of Object.keys(agg)) {
    const it = agg[k];
    fields[k] = {
      key: it.key,
      label: it.label,
      type: it.type,
      usageCount: it.count
    };
  }

  // 4) Écrire ou vider le doc
  const refDoc = doc(db, 'users', uid, 'dossier_fields', dossierKey);
  if (Object.keys(fields).length === 0) {
    try { await deleteDoc(refDoc); } catch { }
    return;
  }
  await setDoc(refDoc, { dossierKey, fields, updatedAt: serverTimestamp() }, { merge: true });
};

/**
 * [info] Rebuild global de tous les dossier_fields
 * Scanne tous les modèles, groupe par dossier et réécrit chaque registry.
 */
export const rebuildAllDossierFields = async (uid) => {
  if (!uid) return;

  // 1) Lire tous les modèles
  const modelCol = collection(db, 'users', uid, 'modeles');
  const modelsSnap = await getDocs(modelCol);

  // 2) Agréger par dossier
  const byDossier = new Map(); // dossierKey -> Map<fieldKey, {key,label,type,count}>
  modelsSnap.forEach(d => {
    const data = d.data() || {};
    const dossierKey = data.dossier || '';
    if (!dossierKey) return;
    if (!byDossier.has(dossierKey)) byDossier.set(dossierKey, new Map());
    const m = byDossier.get(dossierKey);
    const fm = data.fields_meta || {};
    for (const k of Object.keys(fm)) {
      const r = fm[k] || {};
      if (!r?.key) continue;
      if (!m.has(k)) m.set(k, { key: r.key, label: String(r.label || r.key), type: String(r.type || 'text'), count: 0 });
      m.get(k).count += 1;
    }
  });

  // 3) Écrire/mettre à jour les registres présents
  const presentKeys = new Set();
  const writes = [];
  for (const [dossierKey, m] of byDossier.entries()) {
    presentKeys.add(dossierKey);
    const fields = {};
    for (const [k, it] of m.entries()) {
      fields[k] = { key: it.key, label: it.label, type: it.type, usageCount: it.count };
    }
    const refDoc = doc(db, 'users', uid, 'dossier_fields', dossierKey);
    writes.push(setDoc(refDoc, { dossierKey, fields, updatedAt: serverTimestamp() }, { merge: true }));
  }
  await Promise.all(writes);

  // 4) Supprimer les registres orphelins (dossiers qui n'ont plus AUCUN modèle)
  const dfCol = collection(db, 'users', uid, 'dossier_fields');
  const existingSnap = await getDocs(dfCol);
  const deletions = [];
  existingSnap.forEach(d => {
    const key = d.id;
    if (!presentKeys.has(key)) {
      deletions.push(deleteDoc(d.ref));
    }
  });
  await Promise.all(deletions);
};

// --- Dashboard settings (Firestore) ---
export const listenDashboardSettings = (uid, cb) => {
  const refDoc = doc(db, "users", uid, "settings", "dashboard_settings");
  return onSnapshot(refDoc, (snap) => {
    cb(snap.exists() ? snap.data() : null);
  }, (err) => console.error("listenDashboardSettings:", err));
};
// *... Hybride: rend le cache immédiatement puis sync live
export const listenDashboardSettingsHybrid = (uid, cb) => {
  if (!uid) return () => { };
  const refDoc = doc(db, "users", uid, "settings", DASHBOARD_SETTINGS_KEY);
  const CK = dashboardSettingsCacheKey(uid);
  // 1) cache immédiat
  try {
    const cached = localStorage.getItem(CK);
    if (cached) cb(JSON.parse(cached));
  } catch { }
  // 2) live
  return onSnapshot(refDoc, (snap) => {
    const data = snap.exists() ? snap.data() : null;
    if (data) { try { localStorage.setItem(CK, JSON.stringify(data)); } catch { } }
    cb(data);
  }, (err) => console.error("listenDashboardSettingsHybrid:", err));
};
export const loadDashboardSettings = async (uid) => {
  return await loadUserSetting(uid, "dashboard_settings");
};

export const saveDashboardSettings = async (uid, data) => {
  await saveUserSetting(uid, DASHBOARD_SETTINGS_KEY, data);
  // best-effort: maj cache local pour rendu instantané
  try {
    localStorage.setItem(dashboardSettingsCacheKey(uid), JSON.stringify(data));
  } catch { }
};
// --- Relations data: users/{uid}/relations/{typeKey}/rows ---
export const listenRelationRows = (uid, typeKey, cb, opts = {}) => {
  if (!uid || !typeKey) { cb([]); return () => { }; }
  const { pageSize = PAGE_SIZES.relationRows } = opts;

  const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
  const qy = query(colRef, orderBy('createdAt', 'desc'), limit(pageSize));

  return onSnapshot(qy, (snap) => {
    const out = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(out);
  }, (err) => {
    console.error('listenRelationRows error:', err?.message || err);
    cb([]);
  });
};

// *... Hybride: cache immédiat puis fetch ponctuel pour minimiser les lectures
export function listenRelationRowsHybrid(uid, typeKey, cb, opts = {}) {
  if (!uid || !typeKey) { cb([]); return () => { }; }
  const { pageSize = PAGE_SIZES.relationRows } = opts;
  const CK = relationRowsCacheKey(uid, typeKey);

  // Step 1: emit cache immediately
  try {
    const cached = localStorage.getItem(CK);
    if (cached) cb(JSON.parse(cached));
  } catch { }

  try {
    const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
    const qy = query(colRef, orderBy('createdAt', 'desc'), limit(pageSize));

    const unsubscribe = onSnapshot(qy, (snap) => {
      const out = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      try { localStorage.setItem(CK, JSON.stringify(out)); } catch { }
      cb(out);
    }, (err) => {
      console.error('listenRelationRowsHybrid error:', err?.message || err);
    });

    return unsubscribe;
  } catch (err) {
    console.error('listenRelationRowsHybrid setup error:', err?.message || err);
    return () => { };
  }
};
export async function getRelationRowsPage(uid, typeKey, { afterDoc = null, pageSize = PAGE_SIZES.relationRows } = {}) {
  if (!uid || !typeKey) return { items: [], cursor: null };
  const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
  let qy = query(colRef, orderBy('createdAt', 'desc'), limit(pageSize));
  if (afterDoc) qy = query(qy, startAfter(afterDoc));
  const snap = await getDocs(qy);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data(), __snap: d }));
  const cursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, cursor };
}

export const addRelationRow = async (uid, typeKey, payload) => {
  if (!uid || !typeKey) throw new Error("uid/typeKey manquants");
  const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
  try {
    const ref = await addDoc(colRef, {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: ref.id };
  } catch (e) {
    console.error("addRelationRow error:", e?.code || e?.message || e);
    // [info] ici, montrer un toast : "Permission refusée" si e.code === 'permission-denied'
    throw e;
  }
};

export const updateRelationRow = async (uid, typeKey, id, payload) => {
  if (!uid || !typeKey || !id) throw new Error("uid/typeKey/id manquants");
  const ref = doc(db, 'users', uid, 'relations', typeKey, 'rows', id);
  await updateDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp(),
  });
};

export const deleteRelationRow = async (uid, typeKey, id) => {
  if (!uid || !typeKey || !id) throw new Error("uid/typeKey/id manquants");
  const ref = doc(db, 'users', uid, 'relations', typeKey, 'rows', id);
  await deleteDoc(ref);
};

export const cascadeDeleteRelationType = async (uid, typeKey) => {
  if (!uid || !typeKey) return { deletedRows: 0 };
  const label = `cascadeDeleteRelationType:${typeKey}`;
  const collectionRef = collection(db, "users", uid, "relations", typeKey, "rows");

  const deletedRows = await deleteQueryByChunks({
    collectionRef,
    label,
  });

  try {
    await deleteDoc(doc(db, "users", uid, "relations", typeKey));
  } catch (err) {
    if (err?.code !== "not-found") {
      console.warn(`[${label}] failed to delete parent relation doc:`, err?.message || err);
    }
  }

  try {
    localStorage.removeItem(relationRowsCacheKey(uid, typeKey));
  } catch { }

  return { deletedRows };
};
export const addRelationRowsBatch = async (uid, typeKey, rows = []) => {
  if (!uid || !typeKey || !rows.length) return 0;
  const batch = writeBatch(db);
  const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
  for (const r of rows) {
    const ref = doc(colRef);
    batch.set(ref, {
      ...r,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  return rows.length;
};
export const migrateRelationsSettingDocToCollection = async (uid, typeKey, { clearOld = false } = {}) => {
  if (!uid || !typeKey) return { moved: 0 };
  const legacy = await loadUserSetting(uid, `relations_data_${typeKey}`);
  const rows = Array.isArray(legacy?.rows) ? legacy.rows : [];
  if (!rows.length) return { moved: 0 };

  const batch = writeBatch(db);
  const colRef = collection(db, 'users', uid, 'relations', typeKey, 'rows');
  for (const r of rows) {
    const ref = doc(colRef);
    batch.set(ref, {
      ...r,
      // on conserve si déjà numériques/ISO ; sinon on laisse le serveur fixer
      createdAt: r.createdAt ?? serverTimestamp(),
      updatedAt: r.updatedAt ?? serverTimestamp(),
    });
  }
  await batch.commit();

  if (clearOld) {
    try {
      await saveUserSetting(uid, `relations_data_${typeKey}`, { rows: [] });
    } catch { }
  }
  return { moved: rows.length };
};
// [info] Pagination "Voir plus" (non live) pour modèles
export async function getModelesPage(uid, { afterDoc = null, pageSize = PAGE_SIZES.modeles } = {}) {
  if (!uid) return { items: [], cursor: null };
  let qy = query(col(uid, 'modeles'), orderBy('updatedAt', 'desc'), limit(pageSize));
  if (afterDoc) qy = query(qy, startAfter(afterDoc));
  const snap = await getDocs(qy);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data(), __snap: d }));
  const cursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, cursor };
}
export async function getDossierRecordsPage(uid, {
  dossierKey = null,
  statut = null,
  windowDays = null,
  afterDoc = null,
  pageSize = PAGE_SIZES.dossierRecords,
} = {}) {
  if (!uid) return { items: [], cursor: null };

  const colRef = collection(db, "users", uid, "dossier_records");
  const FALLBACK_LIMIT = Math.max(pageSize, 60);

  const baseFilters = [];
  if (statut) baseFilters.push(where("statut", "==", statut));
  if (windowDays && Number(windowDays) > 0) {
    const since = new Date(Date.now() - Number(windowDays) * 86400000);
    baseFilters.push(where("updatedAt", ">=", Timestamp.fromDate(since)));
  }

  const applyFilters = (docs) =>
    docs
      .map((d) => ({ id: d.id, ...d.data(), __snap: d }))
      .filter((row) => recordMatchesFilters(row, { dossierKey, statut, windowDays }));

  const fallbackClauses = (field) => {
    const clauses = [];
    if (field && dossierKey) clauses.push(where(field, "==", dossierKey));
    if (statut) clauses.push(where("statut", "==", statut));
    return clauses;
  };

  if (afterDoc) {
    let ordered = query(
      colRef,
      ...(dossierKey ? [where("dossierKey", "==", dossierKey)] : []),
      ...baseFilters,
      orderBy("updatedAt", "desc")
    );
    ordered = query(ordered, startAfter(afterDoc));
    ordered = query(ordered, limit(pageSize));

    const fallbackQuery = () =>
      getDocs(
        query(
          colRef,
          ...fallbackClauses(dossierKey ? "dossierKey" : null),
          limit(FALLBACK_LIMIT)
        )
      );

    const { snap, fromFallback } = await runQueryWithIndexFallback(
      () => getDocs(ordered),
      fallbackQuery
    );

    const items = applyFilters(snap.docs);
    const cursor = fromFallback || !snap.docs.length
      ? null
      : snap.docs[snap.docs.length - 1];
    return { items, cursor };
  }

  const snapshots = [];
  let primaryFromFallback = false;

  if (dossierKey) {
    const orderedMain = query(
      colRef,
      where("dossierKey", "==", dossierKey),
      ...baseFilters,
      orderBy("updatedAt", "desc"),
      limit(pageSize)
    );
    const mainMeta = await runQueryWithIndexFallback(
      () => getDocs(orderedMain),
      () =>
        getDocs(
          query(
            colRef,
            ...fallbackClauses("dossierKey"),
            limit(FALLBACK_LIMIT)
          )
        )
    );
    snapshots.push(mainMeta.snap);
    primaryFromFallback = mainMeta.fromFallback;

    const orderedLegacy = query(
      colRef,
      where("dossier", "==", dossierKey),
      ...baseFilters,
      orderBy("updatedAt", "desc"),
      limit(pageSize)
    );
    const legacyMeta = await runQueryWithIndexFallback(
      () => getDocs(orderedLegacy),
      () =>
        getDocs(
          query(
            colRef,
            ...fallbackClauses("dossier"),
            limit(FALLBACK_LIMIT)
          )
        )
    );
    snapshots.push(legacyMeta.snap);
  } else {
    const ordered = query(
      colRef,
      ...baseFilters,
      orderBy("updatedAt", "desc"),
      limit(pageSize)
    );
    const meta = await runQueryWithIndexFallback(
      () => getDocs(ordered),
      () => getDocs(query(colRef, ...fallbackClauses(null), limit(FALLBACK_LIMIT)))
    );
    snapshots.push(meta.snap);
    primaryFromFallback = meta.fromFallback;
  }

  const rows = mergeDossierRecordSnapshots(snapshots, {
    limitTo: pageSize,
    includeSnap: true,
  }).filter((row) => recordMatchesFilters(row, { dossierKey, statut, windowDays }));

  let cursor = null;
  if (!primaryFromFallback) {
    const primarySnap = snapshots[0];
    if (primarySnap && primarySnap.docs.length === pageSize) {
      cursor = primarySnap.docs[primarySnap.docs.length - 1];
    }
  }

  return { items: rows, cursor };
}
// ---------------------------------------------------------
// OWNER HELPERS: KPI & Users/Access (v1 mono-tenant)
// ---------------------------------------------------------

const ownerAdminCol = (ownerUid, sub) => {
  if (sub === "roles") return collection(db, "users", ownerUid, "__owner_admin_roles");
  if (sub === "invites") return collection(db, "users", ownerUid, "__owner_admin_invites");
  throw new Error("ownerAdminCol: sub must be 'roles' or 'invites'");
};
const daysAgoTs = (days = 30) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return Timestamp.fromDate(d);
};

// ------ KPI: counts (mono-tenant: sous l'UID owner) ------

export async function countUsersFromRoles(ownerUid) {
  try {
    const cnt = await getCountFromServer(ownerAdminCol(ownerUid, "roles"));
    return cnt.data().count || 0;
  } catch {
    const snap = await getDocs(query(ownerAdminCol(ownerUid, "roles"), limit(1000)));
    return snap.size;
  }
}

export async function countDocsLastNDays(ownerUid, days = 30) {
  const since = daysAgoTs(days);
  try {
    const q = query(
      collection(db, "users", ownerUid, "documents"),
      where("createdAt", ">=", since)
    );
    const cnt = await getCountFromServer(q);
    return cnt.data().count || 0;
  } catch {
    const snap = await getDocs(
      query(
        collection(db, "users", ownerUid, "documents"),
        where("createdAt", ">=", since),
        limit(1000)
      )
    );
    return snap.size;
  }
}

export async function countDossiersUpdatedLastNDays(ownerUid, days = 30) {
  const since = daysAgoTs(days);
  try {
    const q = query(
      collection(db, "users", ownerUid, "dossier_records"),
      where("updatedAt", ">=", since)
    );
    const cnt = await getCountFromServer(q);
    return cnt.data().count || 0;
  } catch {
    const snap = await getDocs(
      query(
        collection(db, "users", ownerUid, "dossier_records"),
        where("updatedAt", ">=", since),
        limit(1000)
      )
    );
    return snap.size;
  }
}

/** Regroupe les KPIs utiles pour l'Overview */
export async function getOwnerOverviewStats(ownerUid) {
  const [users, docs30, dossiers30] = await Promise.all([
    countUsersFromRoles(ownerUid),
    countDocsLastNDays(ownerUid, 30),
    countDossiersUpdatedLastNDays(ownerUid, 30),
  ]);
  return { users, docs30, dossiers30 };
}

/** Liste de documents récents (30j) */
export async function getRecentDocs(ownerUid, { days = 30, limitTo = 20 } = {}) {
  const since = daysAgoTs(days);
  const snap = await getDocs(
    query(
      collection(db, "users", ownerUid, "documents"),
      where("createdAt", ">=", since),
      orderBy("createdAt", "desc"),
      limit(limitTo)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ------ Users & Access (v1 sans claims/CF) ------
// Stockage local sous /users/{ownerUid}/__owner_admin/{roles|invites}

export async function listOwnerRoles(ownerUid) {
  const snap = await getDocs(query(ownerAdminCol(ownerUid, "roles"), orderBy("updatedAt", "desc"), limit(500)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export function listenOwnerRoles(ownerUid, cb) {
  return onSnapshot(
    query(ownerAdminCol(ownerUid, "roles"), orderBy("updatedAt", "desc")),
    (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
}

export async function upsertOwnerRole(ownerUid, { userId, email, role = "user", suspended = false, displayName = "" }) {
  const ref = doc(db, "users", ownerUid, "__owner_admin_roles", userId);
  const now = serverTimestamp();
  const exists = await getDoc(ref);
  if (exists.exists()) {
    await updateDoc(ref, { email, role, suspended, displayName, updatedAt: now });
  } else {
    await setDoc(ref, { email, role, suspended, displayName, createdAt: now, updatedAt: now });
  }
}

export async function setUserRole(ownerUid, userId, role) {
  const ref = doc(db, "users", ownerUid, "__owner_admin_roles", userId);
  await updateDoc(ref, { role, updatedAt: serverTimestamp() });
}

export async function setUserSuspended(ownerUid, userId, suspended) {
  const ref = doc(db, "users", ownerUid, "__owner_admin_roles", userId);
  await updateDoc(ref, { suspended: !!suspended, updatedAt: serverTimestamp() });
}

// Invitations (facultatif v1)
export async function listOwnerInvites(ownerUid) {
  const snap = await getDocs(query(ownerAdminCol(ownerUid, "invites"), orderBy("createdAt", "desc"), limit(200)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createOwnerInvite(ownerUid, { email, role = "user" }) {
  await addDoc(ownerAdminCol(ownerUid, "invites"), {
    email, role, createdAt: serverTimestamp()
  });
}

export async function deleteOwnerInvite(ownerUid, inviteId) {
  await deleteDoc(doc(db, "users", ownerUid, "__owner_admin_roles", "invites", inviteId));
}
